import { canadaHeroData, canadaStripeUrl } from "../../data/canadaTaxData";
import { CheckCircle2, ArrowRight, Clock, Zap } from "lucide-react";

export const CanadaHeroSection = () => (
  <section className="relative bg-white overflow-hidden py-20 px-6 text-center">
    {/* Subtle red radial glow */}
    <div
      className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] pointer-events-none"
      style={{ background: "radial-gradient(ellipse at top, rgba(200,40,58,0.06) 0%, transparent 70%)" }}
    />

    <div className="relative max-w-3xl mx-auto">
      {/* Eyebrow badge */}
      <div className="inline-flex items-center gap-2 bg-[#FDF1F2] border border-[#C8283A]/20 text-[#C8283A] text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full mb-8" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
        <span className="w-1.5 h-1.5 rounded-full bg-[#C8283A] animate-pulse" />
        {canadaHeroData.eyebrow}
      </div>

      {/* Headline */}
      <h1 className="font-black text-[#0B1829] leading-none mb-6" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(2.4rem, 2rem + 3vw, 4.5rem)", letterSpacing: "-0.04em" }}>
        {canadaHeroData.headline1}
        <br />
        <span className="text-[#C8283A]">{canadaHeroData.headline2}</span>
        <br />
        <span className="line-through text-[#B0BEC5]" style={{ fontSize: "0.6em" }}>{canadaHeroData.headlineStrike}</span>
      </h1>

      {/* Sub */}
      <p className="text-[#5A6A7A] text-base sm:text-lg leading-relaxed mb-10 max-w-xl mx-auto font-normal" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
        Built for{" "}
        <strong className="text-[#0B1829] font-bold">Indian immigrants & students in Canada.</strong>
        <br />
        No confusion. No hidden fees. No CRA stress.
        <br />
        Filed in{" "}
        <strong className="text-[#0B1829] font-bold">48 hours</strong>{" "}
        by a Chartered Accountant.
      </p>

      {/* Price Box */}
      <div className="relative inline-block mb-10">
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#D4A843] text-[#0B1829] text-[10px] font-black uppercase tracking-widest px-4 py-1 rounded-full whitespace-nowrap" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
          Flat Fee. No Surprises.
        </div>
        <div className="bg-[#FAFCFF] border-2 border-[#D4A843] rounded-2xl px-12 py-6 text-center shadow-lg shadow-[#D4A843]/10">
          <div className="flex items-start justify-center gap-1">
            <span className="text-[#D4A843] font-black text-2xl mt-3" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>$</span>
            <span className="text-[#D4A843] font-black leading-none" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(4rem, 3rem + 3vw, 5.5rem)", letterSpacing: "-0.04em" }}>40</span>
          </div>
          <p className="text-[#8BA3BE] text-sm font-medium mt-1" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
            {canadaHeroData.priceUnit}
          </p>
          <p className="text-[#8BA3BE] text-xs mt-2" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
            Typical filing fees: <span className="line-through text-[#C8283A]/70">$150–$250</span>
            {" "}· You save:{" "}
            <strong className="text-[#D4A843] font-bold">$110–$210</strong>
          </p>
        </div>
      </div>

      {/* CTA buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
        <a
          href={canadaStripeUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="canada-hero-cta"
          className="group inline-flex items-center gap-2.5 bg-[#C8283A] text-white font-black text-sm sm:text-base px-8 py-4 rounded-xl transition-all duration-300 hover:bg-[#A01828] hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-[#C8283A]/30 uppercase tracking-tight"
          style={{ fontFamily: "'Inter', Arial, sans-serif" }}
        >
          <Zap size={16} className="flex-shrink-0" />
          {canadaHeroData.ctaPrimary}
          <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5 flex-shrink-0" />
        </a>
        <button
          onClick={() => document.querySelector("#how")?.scrollIntoView({ behavior: "smooth" })}
          className="inline-flex items-center gap-2 bg-[#F3F5F8] text-[#5A6A7A] hover:text-[#0B1829] font-semibold text-sm px-7 py-4 rounded-xl transition-all duration-200 hover:bg-[#E8ECF0] uppercase tracking-tight"
          style={{ fontFamily: "'Inter', Arial, sans-serif" }}
        >
          {canadaHeroData.ctaSecondary}
          <ArrowRight size={14} />
        </button>
      </div>

      {/* Trust items */}
      <div className="flex flex-wrap justify-center items-center gap-6">
        {canadaHeroData.trustItems.map((item, i) => (
          <span key={i} className="flex items-center gap-2 text-[#8BA3BE] text-xs font-medium" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
            <CheckCircle2 size={14} className="text-[#D4A843] flex-shrink-0" />
            {item}
          </span>
        ))}
      </div>
    </div>
  </section>
);
